import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import WelcomeHeader from './components/WelcomeHeader';
import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import RegisterPrompt from './components/RegisterPrompt';
import TrustSignals from './components/TrustSignals';
import { useAuth } from '../../contexts/AuthContext';
import { pageVariants, pageTransition, staggerContainer, fadeUpItem, scaleInItem } from '../../components/PageTransition';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <>
      <Helmet>
        <title>Sign In - PrimeOne+ | Your Wellness Journey Starts Here</title>
        <meta name="description" content="Sign in to PrimeOne+ to continue your personalized health and wellness tracking journey. Secure access to your health metrics, goals, and progress reports." />
        <meta name="keywords" content="login, sign in, health tracking, wellness app, PrimeOne+" />
      </Helmet>
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
        className="min-h-screen bg-background"
      >
        {/* Main Content Container */}
        <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8">
          <div className="w-full max-w-6xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              {/* Left Column - Welcome & Form */}
              <motion.div variants={fadeUpItem} className="order-2 lg:order-1">
                <WelcomeHeader />
                <motion.div
                  variants={scaleInItem}
                  whileHover={{ y: -2, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                  transition={{ duration: 0.2 }}
                  className="rounded-2xl"
                >
                  <LoginForm />
                </motion.div>
                <motion.div variants={fadeUpItem}>
                  <SocialLogin />
                </motion.div>
              </motion.div>

              {/* Right Column - Register Prompt */}
              <motion.div
                variants={scaleInItem}
                className="order-1 lg:order-2 flex flex-col items-center justify-center"
                whileHover={{ y: -3, boxShadow: '0 24px 48px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.25 }}
              >
                <RegisterPrompt />
              </motion.div>
            </motion.div>

            {/* Trust Signals */}
            <motion.div
              variants={fadeUpItem}
              initial="initial"
              animate="animate"
              transition={{ delay: 0.4 }}
            >
              <TrustSignals />
            </motion.div>
          </div>
        </div>

        {/* Background Decorative Elements */}
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 bg-accent/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
      </motion.div>
    </>
  );
};

export default LoginPage;